import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config.js';
import { validatePassword, checkPasswordRequirements } from '../utils/passwordValidation';

export default function WelcomeInvitation() {
  const { t } = useTranslation();
  const { token: tokenParam } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitationData, setInvitationData] = useState(null);
  const [step, setStep] = useState('welcome'); // 'welcome' or 'setup'
  
  // Setup form state
  const [setupForm, setSetupForm] = useState({
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [setupLoading, setSetupLoading] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Extract token from URL - handle both route param and direct URL decoding
  const getToken = () => {
    // First try the route parameter
    if (tokenParam) {
      // Decode URL-encoded token (may be encoded multiple times)
      try {
        let decoded = tokenParam;
        // Handle multiple levels of encoding
        while (decoded.includes('%')) {
          const prev = decoded;
          decoded = decodeURIComponent(decoded);
          if (prev === decoded) break; // No more encoding to decode
        }
        return decoded;
      } catch (e) {
        return tokenParam;
      }
    }
    
    // Fallback: extract from current URL path (handles encoded paths)
    try {
      const path = decodeURIComponent(window.location.pathname);
      
      // Try to extract token from decoded path
      if (path.startsWith('/welcome/')) {
        const tokenFromPath = path.replace('/welcome/', '').split('/')[0];
        return tokenFromPath;
      }
      if (path.startsWith('/accept-invitation/')) {
        const tokenFromPath = path.replace('/accept-invitation/', '').split('/')[0];
        return tokenFromPath;
      }
      
      // Try original encoded path
      const encodedPath = window.location.pathname;
      if (encodedPath.includes('welcome')) {
        const parts = encodedPath.split('welcome');
        if (parts.length > 1) {
          // Extract token from after 'welcome'
          const tokenPart = parts[1].replace(/^[\/%2F]+/, '').split(/[\/%2F]/)[0];
          try {
            return decodeURIComponent(tokenPart);
          } catch (e) {
            return tokenPart;
          }
        }
      }
    } catch (e) {
      console.error('Error extracting token from URL:', e);
    }
    
    return null;
  };

  const token = getToken();

  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        setError('Nedostaje token za pozivnicu.');
        setLoading(false);
        return;
      }

      try {
        // Encode token for URL (in case it contains special characters)
        const encodedToken = encodeURIComponent(token);
        // Validate token with backend
        const response = await fetch(`${API_ENDPOINTS.ORGANIZATION_VALIDATE_INVITATION}/${encodedToken}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Neispravan token za pozivnicu.');
        }

        setInvitationData({
          email: data.email,
          companyName: data.companyName,
          companyId: data.companyId,
          userId: data.userId
        });
        setLoading(false);
      } catch (err) {
        console.error('Error validating invitation:', err);
        setError(err.message || 'Neispravan token za pozivnicu.');
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token]);

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setSetupLoading(true);
    setError('');

    // Validate passwords match
    if (setupForm.password !== setupForm.confirmPassword) {
      setError('Lozinke se ne poklapaju.');
      setSetupLoading(false);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(setupForm.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join(' '));
      setSetupLoading(false);
      return;
    }

    try {
      // Encode token for sending to backend
      const encodedToken = encodeURIComponent(token);
      const response = await fetch(API_ENDPOINTS.ORGANIZATION_ACCEPT_INVITATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token, // Send raw token, not encoded
          password: setupForm.password,
          firstName: setupForm.firstName,
          lastName: setupForm.lastName,
          phone: setupForm.phone
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Greška pri aktivaciji naloga');
      }

      // Redirect to login with success message
      navigate('/login', { 
        state: { 
          message: 'Nalog je uspešno aktiviran! Možete se prijaviti sa svojom email adresom i novom lozinkom.' 
        } 
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: '16px', 
          padding: '2rem', 
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 18, color: '#667eea' }}>{t('welcome.validatingInvitation')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: '16px', 
          padding: '2rem', 
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: 24, color: '#dc2626', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '1rem' }}>{t('common.error')}</h2>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{error}</p>
          <button
            onClick={() => navigate('/login')}
            style={{
              background: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            {t('welcome.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'welcome') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{ 
          background: '#fff', 
          borderRadius: '16px', 
          padding: '2rem', 
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px',
          width: '100%'
        }}>
          {/* Welcome Icon */}
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '50%',
            margin: '0 auto 1.5rem auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="40" height="40" fill="#fff" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>

          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 800, 
            color: '#1f2937', 
            marginBottom: '1rem' 
          }}>
            {t('welcome.title')} {t('landing.title')}!
          </h1>

          <p style={{ 
            fontSize: '1.1rem', 
            color: '#6b7280', 
            marginBottom: '1.5rem',
            lineHeight: '1.6'
          }}>
            {t('welcome.acceptInvitation')}
          </p>

          <div style={{ 
            background: '#f8fafc', 
            borderRadius: '12px', 
            padding: '1.5rem', 
            marginBottom: '2rem',
            textAlign: 'left'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#374151' }}>Organizacija:</strong>
              <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                {invitationData?.companyName}
              </span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#374151' }}>Email adresa:</strong>
              <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                {invitationData?.email}
              </span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong style={{ color: '#374151' }}>Status:</strong>
              <span style={{ 
                color: '#059669', 
                marginLeft: '0.5rem',
                background: '#d1fae5',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '14px'
              }}>
                {t('welcome.invitationValid')}
              </span>
            </div>
          </div>

          <button
            onClick={() => setStep('setup')}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {t('welcome.continueSetup')}
          </button>

          <p style={{ 
            fontSize: '14px', 
            color: '#9ca3af', 
            marginTop: '1rem' 
          }}>
            Klikom na dugme pristajete na uslove korišćenja
          </p>
        </div>
      </div>
    );
  }

  // Setup step
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: '16px', 
        padding: '2rem', 
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700, 
            color: '#1f2937', 
            marginBottom: '0.5rem' 
          }}>
            {t('welcome.accountSetup')}
          </h2>
          <p style={{ color: '#6b7280' }}>
            {t('welcome.enterDetails')}
          </p>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '1rem',
            color: '#dc2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSetupSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                Ime
              </label>
              <input
                type="text"
                value={setupForm.firstName}
                onChange={(e) => setSetupForm(prev => ({ ...prev, firstName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                Prezime
              </label>
              <input
                type="text"
                value={setupForm.lastName}
                onChange={(e) => setSetupForm(prev => ({ ...prev, lastName: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                required
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              Telefon (opciono)
            </label>
            <input
              type="tel"
              value={setupForm.phone}
              onChange={(e) => setSetupForm(prev => ({ ...prev, phone: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              Nova lozinka
            </label>
            <input
              type="password"
              value={setupForm.password}
              onChange={(e) => {
                const value = e.target.value;
                setSetupForm(prev => ({ ...prev, password: value }));
                setShowPasswordRequirements(true);
                const requirements = checkPasswordRequirements(value);
                setPasswordRequirements(requirements);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                setShowPasswordRequirements(true);
              }}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              required
            />
            {showPasswordRequirements && setupForm.password && (
              <div style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#6b7280',
                background: '#f8fafc',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '6px', color: '#374151' }}>Zahtevi za lozinku:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ color: passwordRequirements?.length ? '#22c55e' : '#ef4444' }}>
                    {passwordRequirements?.length ? '✓' : '✗'} Najmanje 8 karaktera
                  </div>
                  <div style={{ color: passwordRequirements?.uppercase ? '#22c55e' : '#ef4444' }}>
                    {passwordRequirements?.uppercase ? '✓' : '✗'} Najmanje jedno veliko slovo (A-Z)
                  </div>
                  <div style={{ color: passwordRequirements?.lowercase ? '#22c55e' : '#ef4444' }}>
                    {passwordRequirements?.lowercase ? '✓' : '✗'} Najmanje jedno malo slovo (a-z)
                  </div>
                  <div style={{ color: passwordRequirements?.number ? '#22c55e' : '#ef4444' }}>
                    {passwordRequirements?.number ? '✓' : '✗'} Najmanje jedan broj (0-9)
                  </div>
                  <div style={{ color: passwordRequirements?.special ? '#22c55e' : '#ef4444' }}>
                    {passwordRequirements?.special ? '✓' : '✗'} Najmanje jedan specijalni karakter (!@#$%^&*...)
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              Potvrdi lozinku
            </label>
            <input
              type="password"
              value={setupForm.confirmPassword}
              onChange={(e) => setSetupForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              required
            />
          </div>

          <button
            type="submit"
            disabled={setupLoading}
            style={{
              background: setupLoading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: setupLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              marginTop: '1rem'
            }}
          >
            {setupLoading ? t('welcome.activatingAccount') : t('welcome.activateAccount')}
          </button>
        </form>

        <button
          onClick={() => setStep('welcome')}
          style={{
            background: 'transparent',
            color: '#6b7280',
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer',
            marginTop: '1rem',
            textDecoration: 'underline'
          }}
        >
          {t('welcome.backToWelcome')}
        </button>
      </div>
    </div>
  );
} 